import { Root } from "@radix-ui/react-accordion";
import React, { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { AdvancedConfigV2 } from "src/aiNode/editorComponents/AdvancedConfigV2";
import { ModelSelector } from "src/aiNode/editorComponents/ModelSelector";
import { PromptEditorV2 } from "src/aiNode/editorComponents/PromptEditorV2";
import { ResponseMappingV2 } from "src/aiNode/editorComponents/ResponseMappingV2";
import {
  AiNodeJsonSchemaPropertyUI,
  AiNodeQuota,
  AiNodeV2Form,
  BasicModelCard,
} from "src/aiNode/types";
import {
  convertAiNodeV2BEToForm,
  convertAiNodeV2FormToBE,
} from "src/aiNode/utils";
import {
  useAiNodeQuotas,
  useAvailableAiModels,
} from "src/api/connectApi/queries";
import { useWorkspace } from "src/api/queries";
import { FieldErrors } from "src/api/types";
import {
  accordionRootClassName,
  EditorAccordionItem as AccordionItem,
} from "src/base-components/EditorAccordionItem";
import { LoadingView } from "src/base-components/LoadingView";
import { useDiffViewContext } from "src/changeHistory/DiffViewModal/DiffViewContext";
import { AiNodeV2, AiNodeV2Data } from "src/constants/NodeDataTypes";
import { NodeEditorBaseProps } from "src/nodeEditor/NodeEditor";
import { convertFieldErrorsBeToFe } from "src/utils/FieldErrorUtils";
import { useSubmitForm } from "src/utils/useSubmitForm";

type WrapperProps = {
  node: AiNodeV2;
  workspaceId: string;
} & NodeEditorBaseProps<AiNodeV2Data>;

export const AiNodeV2Editor: React.FC<WrapperProps> = ({
  immutable,
  node,
  workspaceId,
  displayedError,
  isReactive,
  onUpdate,
}) => {
  const workspace = useWorkspace(workspaceId);
  const availableModels = useAvailableAiModels(workspace.data?.base_url);
  const quotas = useAiNodeQuotas(workspace.data?.base_url);
  return (
    <LoadingView
      queryResult={[availableModels, quotas]}
      renderUpdated={([availableModels, quotas]: [
        BasicModelCard[],
        AiNodeQuota,
      ]) => (
        <AiNodeV2EditorInternal
          availableModels={availableModels}
          displayedError={displayedError}
          immutable={immutable}
          isReactive={isReactive}
          node={node}
          quotas={quotas}
          onUpdate={onUpdate}
        />
      )}
    />
  );
};

type Props = {
  node: AiNodeV2;
  availableModels: BasicModelCard[];
  quotas: AiNodeQuota;
} & NodeEditorBaseProps<AiNodeV2Data>;

const AiNodeV2EditorInternal: React.FC<Props> = ({
  availableModels,
  quotas,
  immutable,
  node,
  displayedError,
  isReactive,
  onUpdate,
}) => {
  const { renderedInDiffView } = useDiffViewContext();
  const currentFormValues: AiNodeV2Form = convertAiNodeV2BEToForm(node.data);

  const formProps = useForm({
    defaultValues: currentFormValues,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  // Control the form state externally
  useEffect(() => {
    if (isReactive) {
      const incoming = convertAiNodeV2BEToForm(node.data);
      const existing = formProps.getValues();
      const merged = mergeFormValuesWithExisting(incoming, existing);
      formProps.reset(merged, {
        keepDirty: true,
        keepTouched: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReactive, node.data]);

  useSubmitForm({
    onChange: (data: AiNodeV2Form) => {
      onUpdate({
        newData: convertAiNodeV2FormToBE(data),
      });
    },
    disabled: isReactive,
    previousValues: currentFormValues,
    watch: formProps.watch,
  });

  const runFieldErrors: FieldErrors | undefined = displayedError?.field_errors
    ? convertFieldErrorsBeToFe(displayedError.field_errors)
    : undefined;

  return (
    <FormProvider {...formProps}>
      <form>
        <Root
          className={accordionRootClassName}
          defaultValue={["model", "prompt", "response", "config"]}
          type="multiple"
        >
          <AccordionItem
            className="pb-6 pr-5"
            disabled={immutable || renderedInDiffView}
            title="Model details"
            value="model"
          >
            <ModelSelector
              immutable={immutable}
              models={availableModels}
              quotas={quotas}
            />
          </AccordionItem>
          <AccordionItem
            className="pb-6 pr-5"
            disabled={immutable || renderedInDiffView}
            title="Prompt"
            value="prompt"
          >
            <PromptEditorV2
              immutable={immutable}
              isReactive={isReactive}
              runFieldErrors={runFieldErrors}
            />
          </AccordionItem>
          <AccordionItem
            className="pb-6 pr-5"
            disabled={immutable || renderedInDiffView}
            title="Configure AI response"
            value="response"
          >
            <ResponseMappingV2
              immutable={immutable}
              runFieldErrors={runFieldErrors}
              onUpdate={onUpdate}
            />
          </AccordionItem>
          <AccordionItem
            className="pb-6 pr-5"
            disabled={immutable || renderedInDiffView}
            title="Advanced settings"
            value="config"
          >
            <AdvancedConfigV2
              immutable={immutable}
              isReactive={isReactive}
              models={availableModels}
              runFieldErrors={runFieldErrors}
            />
          </AccordionItem>
        </Root>
      </form>
    </FormProvider>
  );
};

// Consolidate local form state with incoming controlled state.
// This allows keeping empty fields entirely in local state, while still controlling the form with external server state.
// If we don't do this, empty fields in local state would get replaced and suddenly disappear when the component
// rerenders with controlled state.
export const mergeFormValuesWithExisting = (
  incoming: AiNodeV2Form,
  existing: AiNodeV2Form,
): AiNodeV2Form => {
  const merged: AiNodeV2Form = { ...incoming };

  const existingProps =
    existing.inference.response_format.json_schema?.properties ?? [];
  const incomingProps =
    incoming.inference.response_format.json_schema?.properties ?? [];
  const extras = existingProps.filter(
    (field) => field.fieldName == "",
  ) as AiNodeJsonSchemaPropertyUI[];

  const jsonSchema = merged.inference.response_format.json_schema;
  if (jsonSchema) {
    jsonSchema.properties = [...incomingProps, ...extras];
  }

  return merged;
};
